import { forwardRef, useImperativeHandle, useState, useEffect, useRef } from "react"
import { X, ChevronDown, User, Briefcase, Building, FileText, Paperclip, Loader2, Plus, Camera } from "lucide-react"
import { Badge } from "../ui/badge"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog"
import type { Contact } from "./ContactDetailPanel"
import {
  createContact,
  patchContact,
  getContacts,
  uploadTempContactAttachment,
  getTempContactAttachments,
  deleteTempContactAttachment,
  getContactAttachments,
  downloadDocumentFile,
  uploadContactAttachment,
  deleteContactAttachment,
  getTags,
  createTag
} from "../../services/contacts"
import api from "../../api/axios"
import { getTagChipStyles } from "../../utils/tagHelpers"
import { useTheme } from "../../contexts/ThemeContext"
import { capitalizeContactSentences, contactNominativeKey } from "../../utils/contactNormalization"

// Interfaccia per allegati contatto
interface ContactAttachment {
  id: number
  name: string
  file: string
  file_size?: number
  uploaded_at?: string
  node_type?: string
}

// Interfaccia per i tag
interface Tag {
  id: number
  name: string
  color: string
}

// Palette colori per i nuovi tag
const TAG_COLORS = [
  '#7b3fff', // viola
  '#38bdf8', // azzurro
  '#34d399', // verde
  '#fbbf24', // giallo
  '#fb7185', // rosa
  '#e879f9', // magenta
]

interface AvailableContact {
  id: string
  name: string
  type: "person" | "company"
}

interface ContactFormModalProps {
  mode: "create" | "edit"
  contactId?: string
  existingContact?: Contact
  onClose: () => void
  onSave?: (contact: any) => void
  availableContacts?: AvailableContact[]
}

interface NewContactModalProps {
  onClose: () => void
  onSave?: (contact: any) => void
  availableContacts?: AvailableContact[]
}

const EMPTY_CONTACTS: AvailableContact[] = []
const attachmentRows = (data: any): ContactAttachment[] =>
  (Array.isArray(data) ? data : data?.results || []).filter((node: ContactAttachment) => node.node_type !== "FOLDER")

export interface ContactFormHandle { requestClose: () => void }

export const ContactFormModal = forwardRef<ContactFormHandle, ContactFormModalProps>(function ContactFormModal({ mode, contactId, existingContact, onClose, onSave, availableContacts = EMPTY_CONTACTS }, ref) {
  const { theme } = useTheme()
  const [contactType, setContactType] = useState<"person" | "company">(
    mode === "edit" && existingContact ? existingContact.type : "person"
  )
  const [showOtherSection, setShowOtherSection] = useState(false)

  // Form states for person
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [cfPiva, setCfPiva] = useState("")
  const [address, setAddress] = useState("")
  const [role, setRole] = useState("")
  const [companyReference, setCompanyReference] = useState("")

  // Form states for company
  const [companyName, setCompanyName] = useState("")
  const [vatNumber, setVatNumber] = useState("")
  const [taxCode, setTaxCode] = useState("")
  const [legalAddress, setLegalAddress] = useState("")
  const [operativeAddress, setOperativeAddress] = useState("")

  // Phones (shared) - mobile_phone, phone_2, phone_3
  const [phoneCount, setPhoneCount] = useState(1)
  const [mobilePhone, setMobilePhone] = useState("")
  const [phone2, setPhone2] = useState("")
  const [phone3, setPhone3] = useState("")

  // Emails (shared) - email, email_2, email_3
  const [emailCount, setEmailCount] = useState(1)
  const [email, setEmail] = useState("")
  const [email2, setEmail2] = useState("")
  const [email3, setEmail3] = useState("")

  // PEC (shared)
  const [pec, setPec] = useState("")

  // Common
  const [notes, setNotes] = useState("")
  const [websites, setWebsites] = useState<string[]>([""])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Attachments
  const [attachments, setAttachments] = useState<ContactAttachment[]>([])
  const [uploading, setUploading] = useState(false)
  const [loadingAttachments, setLoadingAttachments] = useState(false)
  const [sessionKey] = useState(() => crypto.randomUUID())

  // Avatar
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const avatarInputRef = useRef<HTMLInputElement>(null)

  // Related contacts
  const [linkedContacts, setLinkedContacts] = useState<string[]>([])
  const [contactSearchInput, setContactSearchInput] = useState("")
  const [internalContacts, setInternalContacts] = useState<AvailableContact[]>([])

  // Tags
  const [availableTags, setAvailableTags] = useState<Tag[]>([])
  const [selectedTags, setSelectedTags] = useState<Tag[]>([])
  const [tagInputValue, setTagInputValue] = useState("")
  const [showTagDropdown, setShowTagDropdown] = useState(false)
  const tagInputRef = useRef<HTMLInputElement>(null)
  const tagDropdownRef = useRef<HTMLDivElement>(null)

  // Unsaved changes dialog
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false)
  const [formInitialized, setFormInitialized] = useState(false)
  const initialValuesRef = useRef<Record<string, any> | null>(null)

  // Computed: usa props se disponibili, altrimenti stati interni
  const effectiveContacts = availableContacts?.length ? availableContacts : internalContacts
  const enteredName = contactType === "company" ? companyName : firstName.trim() && lastName.trim() ? `${firstName} ${lastName}` : ""
  const duplicateContact = enteredName && effectiveContacts.find(item =>
    item.id !== contactId && item.type === contactType && contactNominativeKey(item.name) === contactNominativeKey(enteredName))

  // Filter contacts based on search input (exclude self in edit mode)
  const filteredContacts = effectiveContacts.filter(
    (contact) =>
      contact.name.toLowerCase().includes(contactSearchInput.toLowerCase()) &&
      !linkedContacts.includes(contact.id) &&
      (mode !== "edit" || contact.id !== contactId)
  )

  // Load contacts for related contacts search
  useEffect(() => {
    const loadContacts = async () => {
      if (!availableContacts || availableContacts.length === 0) {
        try {
          const res = await getContacts()
          if (res.data && Array.isArray(res.data)) {
            const formatted = res.data.map((c: any) => ({
              id: String(c.id),
              name: c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "N/A",
              type: (c.contact_type === "company" ? "company" : "person") as "person" | "company",
            }))
            setInternalContacts(formatted)
          }
        } catch (error) {
          console.error("Errore caricamento contatti:", error)
        }
      }
    }
    loadContacts()
  }, [availableContacts])

  // Load available tags
  useEffect(() => {
    const loadTags = async () => {
      try {
        const res = await getTags()
        if (res.data && Array.isArray(res.data)) {
          setAvailableTags(res.data)
        }
      } catch (error) {
        console.error("Errore caricamento tag:", error)
      }
    }
    loadTags()
  }, [])

  // Load existing contact data in edit mode
  useEffect(() => {
    if (mode === "edit" && existingContact) {
      // Telefoni
      setMobilePhone(existingContact.mobile_phone || "")
      setPhone2(existingContact.phone_2 || "")
      setPhone3(existingContact.phone_3 || "")

      // Email
      setEmail(existingContact.email || "")
      setEmail2(existingContact.email_2 || "")
      setEmail3(existingContact.email_3 || "")
      setPec(existingContact.pec_email || "")

      // Altri
      setNotes(existingContact.notes || "")
      setWebsites(existingContact.websites?.length ? existingContact.websites : [""])

      // Count visible phone/email fields
      let pCount = 1
      if (existingContact.phone_2) pCount = 2
      if (existingContact.phone_3) pCount = 3
      setPhoneCount(pCount)

      let eCount = 1
      if (existingContact.email_2) eCount = 2
      if (existingContact.email_3) eCount = 3
      setEmailCount(eCount)

      // Related contacts
      const relatedContacts = existingContact.related_contacts || []
      setLinkedContacts(relatedContacts.map((c) => String(c.id)))

      // Tags
      const existingTags = (existingContact as any).tags || []
      setSelectedTags(existingTags)

      // Set contact type for form
      setContactType(existingContact.type)

      if (existingContact.type === "person") {
        setFirstName(existingContact.first_name || "")
        setLastName(existingContact.last_name || "")
        setCfPiva(existingContact.fiscal_code || "")
        setAddress(existingContact.address || "")
        setRole((existingContact as any).role || "")
        setCompanyReference((existingContact as any).company_reference || "")
      } else {
        setCompanyName(existingContact.company_name || "")
        setVatNumber(existingContact.vat_number || "")
        setTaxCode(existingContact.company_fiscal_code || "")
        setLegalAddress(existingContact.legal_address || "")
        setOperativeAddress(existingContact.operational_address || "")
      }

      // Mark form as initialized after populating fields
      setFormInitialized(true)
    } else if (mode === "create") {
      // In create mode, form is immediately initialized
      setFormInitialized(true)
    }
  }, [mode, existingContact])

  // Load attachments based on mode
  useEffect(() => {
    const loadAttachments = async () => {
      if (mode === "edit" && contactId) {
        // Edit mode: load existing attachments
        setLoadingAttachments(true)
        try {
          const response = await getContactAttachments(contactId)
          setAttachments(attachmentRows(response.data))
        } catch (error) {
          console.error("Errore caricamento allegati:", error)
        } finally {
          setLoadingAttachments(false)
        }
      } else {
        // Create mode: load any temp attachments for this session
        setLoadingAttachments(true)
        try {
          const response = await getTempContactAttachments(sessionKey)
          // Map temp attachments to ContactAttachment format
          const mapped = (response.data || []).map((t: any) => ({
            id: t.id,
            name: t.filename,
            file: t.file,
            file_size: t.file_size,
            uploaded_at: t.uploaded_at,
            node_type: 'TEMP'
          }))
          setAttachments(mapped)
        } catch (error) {
          console.error("Errore caricamento allegati temporanei:", error)
        } finally {
          setLoadingAttachments(false)
        }
      }
    }
    loadAttachments()
  }, [mode, contactId, sessionKey])

  // Save initial values after form is initialized (for dirty checking)
  useEffect(() => {
    if (formInitialized && initialValuesRef.current === null) {
        initialValuesRef.current = {
          firstName,
          lastName,
          email,
          mobilePhone,
          phone2,
          phone3,
          email2,
          email3,
          companyReference,
          role,
          pec,
          cfPiva,
          address,
          notes,
          linkedContacts: [...linkedContacts],
          selectedTags: selectedTags.map(t => t.id),
          companyName,
          vatNumber,
          taxCode,
          legalAddress,
          operativeAddress,
          contactType,
          websites: [...websites],
        }
    }
  }, [formInitialized, firstName, lastName, email, mobilePhone, phone2, phone3, email2, email3, companyReference, role, pec, cfPiva, address, notes, linkedContacts, selectedTags, companyName, vatNumber, taxCode, legalAddress, operativeAddress, contactType, websites])

  // Normalize value: treat null, undefined, "" as equivalent
  const normalize = (val: any): string => (val ?? "").toString()

  // Check if form has unsaved changes
  const checkIsDirty = (): boolean => {
    const initial = initialValuesRef.current
    if (!initial) return false
    if (pendingAvatarFile) return true

    // In create mode: dirty if any field is filled
    if (mode === "create") {
      return !!(
        normalize(firstName) || normalize(lastName) || normalize(email) ||
        normalize(mobilePhone) || normalize(phone2) || normalize(phone3) ||
        normalize(email2) || normalize(email3) || normalize(companyReference) ||
        normalize(role) || normalize(pec) || normalize(cfPiva) ||
        normalize(address) || normalize(notes) || linkedContacts.length > 0 ||
        selectedTags.length > 0 || attachments.length > 0 || contactType !== "person" || websites.some(url => url.trim()) ||
        normalize(companyName) || normalize(vatNumber) || normalize(taxCode) ||
        normalize(legalAddress) || normalize(operativeAddress)
      )
    }

    // In edit mode: dirty if any field differs from initial (normalized comparison)
    const linkedSorted = [...linkedContacts].sort().join(',')
    const initialLinkedSorted = [...(initial.linkedContacts || [])].sort().join(',')
    const tagsSorted = selectedTags.map(t => t.id).sort().join(',')
    const initialTagsSorted = [...(initial.selectedTags || [])].sort().join(',')

    return (
      contactType !== initial.contactType ||
      JSON.stringify(websites.filter(url => url.trim())) !== JSON.stringify((initial.websites || []).filter((url: string) => url.trim())) ||
      normalize(firstName) !== normalize(initial.firstName) ||
      normalize(lastName) !== normalize(initial.lastName) ||
      normalize(email) !== normalize(initial.email) ||
      normalize(mobilePhone) !== normalize(initial.mobilePhone) ||
      normalize(phone2) !== normalize(initial.phone2) ||
      normalize(phone3) !== normalize(initial.phone3) ||
      normalize(email2) !== normalize(initial.email2) ||
      normalize(email3) !== normalize(initial.email3) ||
      normalize(companyReference) !== normalize(initial.companyReference) ||
      normalize(role) !== normalize(initial.role) ||
      normalize(pec) !== normalize(initial.pec) ||
      normalize(cfPiva) !== normalize(initial.cfPiva) ||
      normalize(address) !== normalize(initial.address) ||
      normalize(notes) !== normalize(initial.notes) ||
      linkedSorted !== initialLinkedSorted ||
      tagsSorted !== initialTagsSorted ||
      normalize(companyName) !== normalize(initial.companyName) ||
      normalize(vatNumber) !== normalize(initial.vatNumber) ||
      normalize(taxCode) !== normalize(initial.taxCode) ||
      normalize(legalAddress) !== normalize(initial.legalAddress) ||
      normalize(operativeAddress) !== normalize(initial.operativeAddress)
    )
  }

  // Handle close attempt - show dialog if dirty
  const handleAttemptClose = () => {
    if (isSaving || uploading) return
    if (checkIsDirty()) {
      setShowUnsavedDialog(true)
    } else {
      onClose()
    }
  }

  useImperativeHandle(ref, () => ({ requestClose: handleAttemptClose }))

  // Discard changes and close
  const handleDiscardAndClose = () => {
    setShowUnsavedDialog(false)
    onClose()
  }

  // Save and close
  const handleSaveAndClose = async () => {
    setShowUnsavedDialog(false)
    await handleSave()
  }

  // Phone handlers
  const addPhoneField = () => {
    if (phoneCount < 3) {
      setPhoneCount(phoneCount + 1)
    }
  }

  const removePhoneField = (index: number) => {
    if (index === 1) {
      setPhone2(phone3)
      setPhone3("")
      setPhoneCount(phoneCount - 1)
    } else if (index === 2) {
      setPhone3("")
      setPhoneCount(phoneCount - 1)
    }
  }

  // Email handlers
  const addEmailField = () => {
    if (emailCount < 3) {
      setEmailCount(emailCount + 1)
    }
  }

  const removeEmailField = (index: number) => {
    if (index === 1) {
      setEmail2(email3)
      setEmail3("")
      setEmailCount(emailCount - 1)
    } else if (index === 2) {
      setEmail3("")
      setEmailCount(emailCount - 1)
    }
  }

  // Related contacts handlers
  const addLinkedContact = (contactId: string) => {
    if (!linkedContacts.includes(contactId)) {
      setLinkedContacts((prev) => [...prev, contactId])
      setContactSearchInput("")
    }
  }

  const removeLinkedContact = (contactId: string) => {
    setLinkedContacts((prev) => prev.filter((id) => id !== contactId))
  }

  // Upload file handler
  const handleUpload = async (file: File) => {
    setUploading(true)
    try {
      if (mode === "edit" && contactId) {
        // Edit mode: upload directly to contact
        await uploadContactAttachment(contactId, file)
        const response = await getContactAttachments(contactId)
        setAttachments(attachmentRows(response.data))
      } else {
        // Create mode: upload as temp attachment
        await uploadTempContactAttachment(file, sessionKey)
        const response = await getTempContactAttachments(sessionKey)
        const mapped = (response.data || []).map((t: any) => ({
          id: t.id,
          name: t.filename,
          file: t.file,
          file_size: t.file_size,
          uploaded_at: t.uploaded_at,
          node_type: 'TEMP'
        }))
        setAttachments(mapped)
      }
    } catch (error) {
      console.error("Errore upload file:", error)
      setSaveError("Errore durante l'upload del file")
    } finally {
      setUploading(false)
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleUpload(e.target.files[0])
      e.target.value = "" // Reset input
    }
  }

  // Delete attachment handler
  const handleDeleteAttachment = async (attachmentId: number) => {
    try {
      if (mode === "edit" && contactId) {
        // Edit mode: delete from contact
        await deleteContactAttachment(attachmentId)
        const response = await getContactAttachments(contactId)
        setAttachments(attachmentRows(response.data))
      } else {
        // Create mode: delete temp attachment
        await deleteTempContactAttachment(attachmentId)
        const response = await getTempContactAttachments(sessionKey)
        const mapped = (response.data || []).map((t: any) => ({
          id: t.id,
          name: t.filename,
          file: t.file,
          file_size: t.file_size,
          uploaded_at: t.uploaded_at,
          node_type: 'TEMP'
        }))
        setAttachments(mapped)
      }
    } catch (error) {
      console.error("Errore eliminazione allegato:", error)
    }
  }

  // Download attachment handler
  const handleDownloadAttachment = async (attachment: ContactAttachment) => {
    try {
      // Download via axios to handle auth
      const response = attachment.node_type === 'TEMP'
        ? await api.get(attachment.file, { responseType: 'blob' })
        : await downloadDocumentFile(attachment.id)
      const blobUrl = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = attachment.name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error("Errore download file:", error)
      setSaveError("Impossibile scaricare l'allegato. Riprova.")
    }
  }

  // Avatar change handler
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setPendingAvatarFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // Get avatar initials
  const getAvatarInitials = (): string => {
    if (contactType === "person") {
      const f = firstName?.charAt(0)?.toUpperCase() || ""
      const l = lastName?.charAt(0)?.toUpperCase() || ""
      return f + l || "?"
    } else {
      return companyName?.charAt(0)?.toUpperCase() || "?"
    }
  }

  // Get existing avatar URL (for edit mode)
  const existingAvatarUrl = mode === "edit" && existingContact ? (existingContact as any).avatar_url : null

  const handleSave = async () => {
    if (isSaving || uploading) return
    if (duplicateContact) {
      setSaveError("Esiste già un contatto con questo nominativo.")
      return
    }
    let contactData: Record<string, any>

    if (contactType === "person") {
      contactData = {
        contact_type: "individual",
        first_name: firstName,
        last_name: lastName,
        mobile_phone: mobilePhone,
        phone_2: phone2 || null,
        phone_3: phone3 || null,
        email: email || null,
        email_2: email2 || null,
        email_3: email3 || null,
        pec_email: pec || null,
        fiscal_code: cfPiva || null,
        address: address || null,
        role: role || null,
        company_reference: companyReference || null,
        notes: notes || null,
        related_contact_ids: linkedContacts.map(id => parseInt(id, 10)),
        tag_ids: selectedTags.map(t => t.id),
      }
    } else {
      contactData = {
        contact_type: "company",
        company_name: companyName,
        mobile_phone: mobilePhone,
        phone_2: phone2 || null,
        phone_3: phone3 || null,
        email: email || null,
        email_2: email2 || null,
        email_3: email3 || null,
        pec_email: pec || null,
        vat_number: vatNumber || null,
        company_fiscal_code: taxCode || null,
        legal_address: legalAddress || null,
        operational_address: operativeAddress || null,
        notes: notes || null,
        related_contact_ids: linkedContacts.map(id => parseInt(id, 10)),
        tag_ids: selectedTags.map(t => t.id),
      }
    }

    contactData.websites = websites.map(url => url.trim()).filter(Boolean).map(url => url.includes('://') ? url : `https://${url}`)
    for (const field of ['first_name', 'last_name', 'company_name', 'role', 'company_reference', 'address', 'legal_address', 'operational_address', 'notes']) {
      if (typeof contactData[field] === 'string') contactData[field] = capitalizeContactSentences(contactData[field])
    }

    // In create mode, include session_key for temp attachments
    if (mode === "create" && attachments.length > 0) {
      contactData.temp_attachments = sessionKey
    }

    setIsSaving(true)
    setSaveError(null)

    try {
      let response

      // If avatar file is present, use FormData multipart
      if (pendingAvatarFile) {
        const formData = new FormData()

        // Add all contact data fields
        Object.entries(contactData).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
              if (key === 'websites') {
                formData.append(key, JSON.stringify(value))
                return
              }
              // For arrays (related_contact_ids), append each value
              value.forEach((v) => formData.append(key, String(v)))
            } else {
              formData.append(key, String(value))
            }
          }
        })

        // Add avatar file
        formData.append('avatar', pendingAvatarFile)

        if (mode === "edit" && contactId) {
          // Don't set Content-Type manually - axios will set it with correct boundary
          response = await api.patch(`api/contacts/contacts/${contactId}/`, formData)
        } else {
          // Don't set Content-Type manually - axios will set it with correct boundary
          response = await api.post('api/contacts/contacts/', formData)
        }
      } else {
        // No avatar, use JSON
        if (mode === "edit" && contactId) {
          response = await patchContact(contactId, contactData)
        } else {
          response = await createContact(contactData)
        }
      }

      onSave?.(response.data)
      onClose()
    } catch (error: any) {
      const errorData = error?.response?.data
      let message = "Errore durante il salvataggio. Riprova."
      if (typeof errorData === 'string') {
        message = errorData
      } else if (errorData?.detail) {
        message = Array.isArray(errorData.detail) ? errorData.detail.join(' ') : errorData.detail
      } else if (typeof errorData === 'object') {
        const firstKey = Object.keys(errorData)[0]
        message = `${firstKey}: ${errorData[firstKey]}`
      }
      setSaveError(message)
    } finally {
      setIsSaving(false)
    }
  }

  // Tag handlers
  const filteredTags = availableTags.filter(
    (tag) =>
      tag.name.toLowerCase().includes(tagInputValue.toLowerCase()) &&
      !selectedTags.some((st) => st.id === tag.id)
  )

  const showCreateOption =
    tagInputValue.trim() &&
    !availableTags.some((t) => t.name.toLowerCase() === tagInputValue.toLowerCase())

  const handleTagSelect = (tag: Tag) => {
    setSelectedTags((prev) => [...prev, tag])
    setTagInputValue("")
    setShowTagDropdown(false)
  }

  const handleTagRemove = (tagId: number) => {
    setSelectedTags((prev) => prev.filter((t) => t.id !== tagId))
  }

  const handleCreateTag = async () => {
    if (!tagInputValue.trim()) return
    try {
      // Usa un colore dalla palette in base al numero di tag esistenti
      const colorIndex = availableTags.length % TAG_COLORS.length
      const newTagData = {
        name: capitalizeContactSentences(tagInputValue.trim()),
        color: TAG_COLORS[colorIndex],
      }
      const res = await createTag(newTagData)
      const newTag = res.data
      setAvailableTags((prev) => [...prev, newTag])
      setSelectedTags((prev) => [...prev, newTag])
      setTagInputValue("")
      setShowTagDropdown(false)
    } catch (error) {
      console.error("Errore creazione tag:", error)
    }
  }

  // Click outside handler for tag dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(event.target as Node) &&
        tagInputRef.current &&
        !tagInputRef.current.contains(event.target as Node)
      ) {
        setShowTagDropdown(false)
      }
    }

    if (showTagDropdown) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showTagDropdown])

  // Reusable phone input with + button
  const renderPhoneInput = (value: string, onChange: (v: string) => void, index: number, placeholder: string) => {
    const isFirst = index === 0
    const canAdd = isFirst && phoneCount < 3
    const canRemove = !isFirst

    return (
      <div className="flex items-center rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] focus-within:border-[var(--hexa-border)] transition">
        <input
          type="tel"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-transparent border-none text-[var(--hexa-text-primary)] placeholder-[var(--hexa-text-muted)] px-3 py-2 text-xs focus:outline-none"
        />
        {canAdd && (
          <button
            type="button"
            onClick={addPhoneField}
            className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition"
          >
            <Plus className="h-3.5 w-3.5 text-[var(--hexa-purple)]" />
          </button>
        )}
        {canRemove && (
          <button
            type="button"
            onClick={() => removePhoneField(index)}
            className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition"
          >
            <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
          </button>
        )}
      </div>
    )
  }

  // Reusable email input with + button
  const renderEmailInput = (value: string, onChange: (v: string) => void, index: number, placeholder: string) => {
    const isFirst = index === 0
    const canAdd = isFirst && emailCount < 3
    const canRemove = !isFirst

    return (
      <div className="flex items-center rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] focus-within:border-[var(--hexa-border)] transition">
        <input
          type="email"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-transparent border-none text-[var(--hexa-text-primary)] placeholder-[var(--hexa-text-muted)] px-3 py-2 text-xs focus:outline-none"
        />
        {canAdd && (
          <button
            type="button"
            onClick={addEmailField}
            className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition"
          >
            <Plus className="h-3.5 w-3.5 text-[var(--hexa-purple)]" />
          </button>
        )}
        {canRemove && (
          <button
            type="button"
            onClick={() => removeEmailField(index)}
            className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition"
          >
            <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
          </button>
        )}
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col h-full bg-[var(--hexa-modal-bg)]" onBlurCapture={event => {
        const element = event.target as HTMLInputElement | HTMLTextAreaElement
        const setters: Record<string, (value: string) => void> = {
          'field-nome': setFirstName, 'field-cognome': setLastName, 'field-ragione-sociale': setCompanyName,
          'field-azienda': setCompanyReference, 'field-ruolo': setRole, 'field-indirizzo': setAddress,
          'field-sede-legale': setLegalAddress, 'field-sede-operativa': setOperativeAddress, 'field-note': setNotes,
        }
        if (setters[element.id]) setters[element.id](capitalizeContactSentences(element.value))
      }}>
        {/* Header */}
        <div className="flex-shrink-0 border-b border-[var(--hexa-border)] px-4 py-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold !text-[var(--hexa-text-primary)]">
              {mode === "create" ? "Nuovo contatto" : "Modifica contatto"}
            </h2>
            <button
              type="button"
              onClick={handleAttemptClose}
              className="h-6 w-6 rounded-lg grid place-items-center text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-topbar-icon-hover)] transition"
              aria-label="Chiudi"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

      {/* Form Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hexa">
        {/* Avatar + Toggle Section */}
        <div className="flex items-center gap-3 mb-3">
          {/* Avatar circle */}
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
            className="hidden"
          />
          <div
            onClick={() => avatarInputRef.current?.click()}
            className="w-[52px] h-[52px] rounded-full border-2 border-dashed border-[var(--hexa-purple)] flex items-center justify-center cursor-pointer overflow-hidden hover:border-[var(--hexa-purple-hover)] transition bg-[var(--hexa-surface-elevated)]"
          >
            {avatarPreview || existingAvatarUrl ? (
              <img
                src={avatarPreview || existingAvatarUrl}
                alt="Avatar"
                className="w-full h-full object-cover"
              />
            ) : getAvatarInitials() !== "?" ? (
              <span className="text-sm font-semibold text-[var(--hexa-text-muted)]">
                {getAvatarInitials()}
              </span>
            ) : (
              <Camera className="w-5 h-5 text-[var(--hexa-purple)]" />
            )}
          </div>

          {/* Toggle - only in create mode */}
          {mode === "create" && (
            <div className="inline-flex p-[3px] rounded-lg bg-[var(--hexa-card-bg)] border border-[var(--hexa-border)]">
              <button
                type="button"
                onClick={() => setContactType("person")}
                title="Persona"
                className={`w-8 h-7 rounded-md flex items-center justify-center transition ${
                  contactType === "person" ? "bg-[var(--hexa-purple)]" : ""
                }`}
              >
                <User className={`w-4 h-4 transition ${
                  contactType === "person" ? "text-white" : "text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-secondary)]"
                }`} />
              </button>
              <button
                type="button"
                onClick={() => setContactType("company")}
                title="Azienda"
                className={`w-8 h-7 rounded-md flex items-center justify-center transition ${
                  contactType === "company" ? "bg-[var(--hexa-purple)]" : ""
                }`}
              >
                <Briefcase className={`w-4 h-4 transition ${
                  contactType === "company" ? "text-white" : "text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-secondary)]"
                }`} />
              </button>
            </div>
          )}
        </div>

        {/* SECTION 1 - Main Information */}
        {contactType === "person" ? (
          <>
            {/* Person fields - 2 columns */}
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <input
                  id="field-nome"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder=" "
                  className="peer w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
                />
                <label
                  htmlFor="field-nome"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--hexa-text-muted)] pointer-events-none transition-all duration-200 peer-focus:top-1 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:text-[#7b3fff] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px]"
                >
                  Nome <span className="text-[#ff6467]">*</span>
                </label>
              </div>
              <div className="relative">
                <input
                  id="field-cognome"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder=" "
                  className="peer w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
                />
                <label
                  htmlFor="field-cognome"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--hexa-text-muted)] pointer-events-none transition-all duration-200 peer-focus:top-1 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:text-[#7b3fff] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px]"
                >
                  Cognome <span className="text-[#ff6467]">*</span>
                </label>
              </div>
            </div>

            {/* Email with + */}
            <div className="space-y-1.5">
              <div className="relative">
                <div className="group/field flex items-center rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] focus-within:border-[var(--hexa-border)] transition">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder=" "
                    className="flex-1 bg-transparent border-none text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none"
                  />
                  {emailCount < 3 && (
                    <button type="button" onClick={addEmailField} className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition">
                      <Plus className="h-3.5 w-3.5 text-[var(--hexa-purple)]" />
                    </button>
                  )}
                </div>
                <label className={`absolute left-3 text-xs pointer-events-none transition-all duration-200 group-focus-within/field:top-1 group-focus-within/field:translate-y-0 group-focus-within/field:text-[11px] group-focus-within/field:text-[#7b3fff] ${email ? 'top-1 translate-y-0 text-[11px] text-[var(--hexa-text-muted)]' : 'top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]'}`}>
                  Email
                </label>
              </div>
              {emailCount >= 2 && (
                <div className="relative">
                  <div className="group/field flex items-center rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] focus-within:border-[var(--hexa-border)] transition">
                    <input type="email" value={email2} onChange={(e) => setEmail2(e.target.value)} placeholder=" " className="flex-1 bg-transparent border-none text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none" />
                    <button type="button" onClick={() => removeEmailField(1)} className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition">
                      <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
                    </button>
                  </div>
                  <label className={`absolute left-3 text-xs pointer-events-none transition-all duration-200 group-focus-within/field:top-1 group-focus-within/field:translate-y-0 group-focus-within/field:text-[11px] group-focus-within/field:text-[#7b3fff] ${email2 ? 'top-1 translate-y-0 text-[11px] text-[var(--hexa-text-muted)]' : 'top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]'}`}>
                    Email 2
                  </label>
                </div>
              )}
              {emailCount >= 3 && (
                <div className="relative">
                  <div className="group/field flex items-center rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] focus-within:border-[var(--hexa-border)] transition">
                    <input type="email" value={email3} onChange={(e) => setEmail3(e.target.value)} placeholder=" " className="flex-1 bg-transparent border-none text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none" />
                    <button type="button" onClick={() => removeEmailField(2)} className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition">
                      <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
                    </button>
                  </div>
                  <label className={`absolute left-3 text-xs pointer-events-none transition-all duration-200 group-focus-within/field:top-1 group-focus-within/field:translate-y-0 group-focus-within/field:text-[11px] group-focus-within/field:text-[#7b3fff] ${email3 ? 'top-1 translate-y-0 text-[11px] text-[var(--hexa-text-muted)]' : 'top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]'}`}>
                    Email 3
                  </label>
                </div>
              )}
            </div>

            {/* Phone with + */}
            <div className="space-y-1.5">
              <div className="relative">
                <div className="group/field flex items-center rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] focus-within:border-[var(--hexa-border)] transition">
                  <input
                    type="tel"
                    value={mobilePhone}
                    onChange={(e) => setMobilePhone(e.target.value)}
                    placeholder=" "
                    className="flex-1 bg-transparent border-none text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none"
                  />
                  {phoneCount < 3 && (
                    <button type="button" onClick={addPhoneField} className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition">
                      <Plus className="h-3.5 w-3.5 text-[var(--hexa-purple)]" />
                    </button>
                  )}
                </div>
                <label className={`absolute left-3 text-xs pointer-events-none transition-all duration-200 group-focus-within/field:top-1 group-focus-within/field:translate-y-0 group-focus-within/field:text-[11px] group-focus-within/field:text-[#7b3fff] ${mobilePhone ? 'top-1 translate-y-0 text-[11px] text-[var(--hexa-text-muted)]' : 'top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]'}`}>
                  Telefono <span className="text-[#ff6467]">*</span>
                </label>
              </div>
              {phoneCount >= 2 && (
                <div className="relative">
                  <div className="group/field flex items-center rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] focus-within:border-[var(--hexa-border)] transition">
                    <input type="tel" value={phone2} onChange={(e) => setPhone2(e.target.value)} placeholder=" " className="flex-1 bg-transparent border-none text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none" />
                    <button type="button" onClick={() => removePhoneField(1)} className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition">
                      <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
                    </button>
                  </div>
                  <label className={`absolute left-3 text-xs pointer-events-none transition-all duration-200 group-focus-within/field:top-1 group-focus-within/field:translate-y-0 group-focus-within/field:text-[11px] group-focus-within/field:text-[#7b3fff] ${phone2 ? 'top-1 translate-y-0 text-[11px] text-[var(--hexa-text-muted)]' : 'top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]'}`}>
                    Telefono 2
                  </label>
                </div>
              )}
              {phoneCount >= 3 && (
                <div className="relative">
                  <div className="group/field flex items-center rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] focus-within:border-[var(--hexa-border)] transition">
                    <input type="tel" value={phone3} onChange={(e) => setPhone3(e.target.value)} placeholder=" " className="flex-1 bg-transparent border-none text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none" />
                    <button type="button" onClick={() => removePhoneField(2)} className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition">
                      <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
                    </button>
                  </div>
                  <label className={`absolute left-3 text-xs pointer-events-none transition-all duration-200 group-focus-within/field:top-1 group-focus-within/field:translate-y-0 group-focus-within/field:text-[11px] group-focus-within/field:text-[#7b3fff] ${phone3 ? 'top-1 translate-y-0 text-[11px] text-[var(--hexa-text-muted)]' : 'top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]'}`}>
                    Telefono 3
                  </label>
                </div>
              )}
            </div>

            {/* Azienda e Ruolo */}
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <input
                  id="field-azienda"
                  type="text"
                  value={companyReference}
                  onChange={(e) => setCompanyReference(e.target.value)}
                  placeholder=" "
                  className="peer w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
                />
                <label
                  htmlFor="field-azienda"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--hexa-text-muted)] pointer-events-none transition-all duration-200 peer-focus:top-1 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:text-[#7b3fff] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px]"
                >
                  Azienda
                </label>
              </div>
              <div className="relative">
                <input
                  id="field-ruolo"
                  type="text"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder=" "
                  className="peer w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
                />
                <label
                  htmlFor="field-ruolo"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--hexa-text-muted)] pointer-events-none transition-all duration-200 peer-focus:top-1 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:text-[#7b3fff] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px]"
                >
                  Ruolo
                </label>
              </div>
            </div>

          </>
        ) : (
          <>
            {/* Company fields */}
            <div className="relative">
              <input
                id="field-ragione-sociale"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder=" "
                className="peer w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
              />
              <label
                htmlFor="field-ragione-sociale"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--hexa-text-muted)] pointer-events-none transition-all duration-200 peer-focus:top-1 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:text-[#7b3fff] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px]"
              >
                Ragione sociale <span className="text-[#ff6467]">*</span>
              </label>
            </div>

            {/* Email with + */}
            <div className="space-y-1.5">
              <div className="relative">
                <div className="group/field flex items-center rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] focus-within:border-[var(--hexa-border)] transition">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder=" "
                    className="flex-1 bg-transparent border-none text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none"
                  />
                  {emailCount < 3 && (
                    <button type="button" onClick={addEmailField} className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition">
                      <Plus className="h-3.5 w-3.5 text-[var(--hexa-purple)]" />
                    </button>
                  )}
                </div>
                <label className={`absolute left-3 text-xs pointer-events-none transition-all duration-200 group-focus-within/field:top-1 group-focus-within/field:translate-y-0 group-focus-within/field:text-[11px] group-focus-within/field:text-[#7b3fff] ${email ? 'top-1 translate-y-0 text-[11px] text-[var(--hexa-text-muted)]' : 'top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]'}`}>
                  Email
                </label>
              </div>
              {emailCount >= 2 && (
                <div className="relative">
                  <div className="group/field flex items-center rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] focus-within:border-[var(--hexa-border)] transition">
                    <input type="email" value={email2} onChange={(e) => setEmail2(e.target.value)} placeholder=" " className="flex-1 bg-transparent border-none text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none" />
                    <button type="button" onClick={() => removeEmailField(1)} className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition">
                      <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
                    </button>
                  </div>
                  <label className={`absolute left-3 text-xs pointer-events-none transition-all duration-200 group-focus-within/field:top-1 group-focus-within/field:translate-y-0 group-focus-within/field:text-[11px] group-focus-within/field:text-[#7b3fff] ${email2 ? 'top-1 translate-y-0 text-[11px] text-[var(--hexa-text-muted)]' : 'top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]'}`}>
                    Email 2
                  </label>
                </div>
              )}
              {emailCount >= 3 && (
                <div className="relative">
                  <div className="group/field flex items-center rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] focus-within:border-[var(--hexa-border)] transition">
                    <input type="email" value={email3} onChange={(e) => setEmail3(e.target.value)} placeholder=" " className="flex-1 bg-transparent border-none text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none" />
                    <button type="button" onClick={() => removeEmailField(2)} className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition">
                      <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
                    </button>
                  </div>
                  <label className={`absolute left-3 text-xs pointer-events-none transition-all duration-200 group-focus-within/field:top-1 group-focus-within/field:translate-y-0 group-focus-within/field:text-[11px] group-focus-within/field:text-[#7b3fff] ${email3 ? 'top-1 translate-y-0 text-[11px] text-[var(--hexa-text-muted)]' : 'top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]'}`}>
                    Email 3
                  </label>
                </div>
              )}
            </div>

            {/* Phone with + */}
            <div className="space-y-1.5">
              <div className="relative">
                <div className="group/field flex items-center rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] focus-within:border-[var(--hexa-border)] transition">
                  <input
                    type="tel"
                    value={mobilePhone}
                    onChange={(e) => setMobilePhone(e.target.value)}
                    placeholder=" "
                    className="flex-1 bg-transparent border-none text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none"
                  />
                  {phoneCount < 3 && (
                    <button type="button" onClick={addPhoneField} className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition">
                      <Plus className="h-3.5 w-3.5 text-[var(--hexa-purple)]" />
                    </button>
                  )}
                </div>
                <label className={`absolute left-3 text-xs pointer-events-none transition-all duration-200 group-focus-within/field:top-1 group-focus-within/field:translate-y-0 group-focus-within/field:text-[11px] group-focus-within/field:text-[#7b3fff] ${mobilePhone ? 'top-1 translate-y-0 text-[11px] text-[var(--hexa-text-muted)]' : 'top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]'}`}>
                  Telefono <span className="text-[#ff6467]">*</span>
                </label>
              </div>
              {phoneCount >= 2 && (
                <div className="relative">
                  <div className="group/field flex items-center rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] focus-within:border-[var(--hexa-border)] transition">
                    <input type="tel" value={phone2} onChange={(e) => setPhone2(e.target.value)} placeholder=" " className="flex-1 bg-transparent border-none text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none" />
                    <button type="button" onClick={() => removePhoneField(1)} className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition">
                      <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
                    </button>
                  </div>
                  <label className={`absolute left-3 text-xs pointer-events-none transition-all duration-200 group-focus-within/field:top-1 group-focus-within/field:translate-y-0 group-focus-within/field:text-[11px] group-focus-within/field:text-[#7b3fff] ${phone2 ? 'top-1 translate-y-0 text-[11px] text-[var(--hexa-text-muted)]' : 'top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]'}`}>
                    Telefono 2
                  </label>
                </div>
              )}
              {phoneCount >= 3 && (
                <div className="relative">
                  <div className="group/field flex items-center rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] focus-within:border-[var(--hexa-border)] transition">
                    <input type="tel" value={phone3} onChange={(e) => setPhone3(e.target.value)} placeholder=" " className="flex-1 bg-transparent border-none text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none" />
                    <button type="button" onClick={() => removePhoneField(2)} className="w-6 h-6 flex-shrink-0 mr-1 rounded flex items-center justify-center hover:bg-[var(--hexa-surface-hover)] transition">
                      <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
                    </button>
                  </div>
                  <label className={`absolute left-3 text-xs pointer-events-none transition-all duration-200 group-focus-within/field:top-1 group-focus-within/field:translate-y-0 group-focus-within/field:text-[11px] group-focus-within/field:text-[#7b3fff] ${phone3 ? 'top-1 translate-y-0 text-[11px] text-[var(--hexa-text-muted)]' : 'top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]'}`}>
                    Telefono 3
                  </label>
                </div>
              )}
            </div>

            <div className="relative">
              <input
                id="field-sede-legale"
                type="text"
                value={legalAddress}
                onChange={(e) => setLegalAddress(e.target.value)}
                placeholder=" "
                className="peer w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
              />
              <label
                htmlFor="field-sede-legale"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--hexa-text-muted)] pointer-events-none transition-all duration-200 peer-focus:top-1 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:text-[#7b3fff] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px]"
              >
                Sede Legale
              </label>
            </div>
          </>
        )}

        <div className="space-y-1.5">
          {websites.map((website, index) => <div key={index} className="flex min-w-0 items-end gap-1">
            <div className="min-w-0 flex-1">
              <label htmlFor={`field-website-${index}`} className="text-xs text-[var(--hexa-text-secondary)]">{index === 0 ? 'Sito web' : `Sito web ${index + 1}`}</label>
              <input id={`field-website-${index}`} type="url" inputMode="url" autoCapitalize="none" autoCorrect="off"
                value={website} onChange={event => setWebsites(prev => prev.map((url, i) => i === index ? event.target.value : url))}
                placeholder="https://www.esempio.it" className="w-full min-w-0 rounded-lg border border-[var(--hexa-input-border)] bg-[var(--hexa-input-bg)] px-3 py-2 text-xs text-[var(--hexa-text-primary)]" />
            </div>
            {index === 0 && websites.length < 3 && <button type="button" aria-label="Aggiungi sito web" className="h-10 w-10 shrink-0" onClick={() => setWebsites(prev => [...prev, ''])}><Plus className="h-4 w-4 mx-auto" /></button>}
            {index > 0 && <button type="button" aria-label={`Rimuovi sito web ${index + 1}`} className="h-10 w-10 shrink-0" onClick={() => setWebsites(prev => prev.filter((_, i) => i !== index))}><X className="h-4 w-4 mx-auto" /></button>}
          </div>)}
        </div>

        {/* SECTION 2 - Altro (collapsible) */}
        <span
          onClick={() => setShowOtherSection(!showOtherSection)}
          className="flex items-center gap-1 text-xs text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-secondary)] cursor-pointer select-none mt-1 mb-2"
        >
          Altro <ChevronDown className={`h-3 w-3 transition-transform ${showOtherSection ? 'rotate-180' : ''}`} />
        </span>
        {showOtherSection && (
          <div className="space-y-3">
            <div className="px-3 pb-3 space-y-3">
              {contactType === "person" ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      <input
                        id="field-cf-piva"
                        type="text"
                        value={cfPiva}
                        onChange={(e) => setCfPiva(e.target.value)}
                        placeholder=" "
                        className="peer w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
                      />
                      <label
                        htmlFor="field-cf-piva"
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--hexa-text-muted)] pointer-events-none transition-all duration-200 peer-focus:top-1 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:text-[#7b3fff] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px]"
                      >
                        CF / P.IVA
                      </label>
                    </div>
                    <div className="relative">
                      <input
                        id="field-indirizzo"
                        type="text"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder=" "
                        className="peer w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
                      />
                      <label
                        htmlFor="field-indirizzo"
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--hexa-text-muted)] pointer-events-none transition-all duration-200 peer-focus:top-1 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:text-[#7b3fff] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px]"
                      >
                        Indirizzo
                      </label>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      <input
                        id="field-partita-iva"
                        type="text"
                        value={vatNumber}
                        onChange={(e) => setVatNumber(e.target.value)}
                        placeholder=" "
                        className="peer w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
                      />
                      <label
                        htmlFor="field-partita-iva"
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--hexa-text-muted)] pointer-events-none transition-all duration-200 peer-focus:top-1 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:text-[#7b3fff] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px]"
                      >
                        Partita IVA
                      </label>
                    </div>
                    <div className="relative">
                      <input
                        id="field-codice-fiscale"
                        type="text"
                        value={taxCode}
                        onChange={(e) => setTaxCode(e.target.value)}
                        placeholder=" "
                        className="peer w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
                      />
                      <label
                        htmlFor="field-codice-fiscale"
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--hexa-text-muted)] pointer-events-none transition-all duration-200 peer-focus:top-1 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:text-[#7b3fff] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px]"
                      >
                        Codice Fiscale
                      </label>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      <input
                        id="field-settore"
                        type="text"
                        disabled
                        aria-describedby="company-extra-fields-info"
                        placeholder=" "
                        className="peer w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
                      />
                      <label
                        htmlFor="field-settore"
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--hexa-text-muted)] pointer-events-none transition-all duration-200 peer-focus:top-1 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:text-[#7b3fff] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px]"
                      >
                        Settore
                      </label>
                    </div>

                  </div>
                  <p id="company-extra-fields-info" className="text-xs text-[var(--hexa-text-muted)]">
                    Il settore non è ancora supportato e non viene salvato.
                  </p>
                  <div className="relative">
                    <input
                      id="field-sede-operativa"
                      type="text"
                      value={operativeAddress}
                      onChange={(e) => setOperativeAddress(e.target.value)}
                      placeholder=" "
                      className="peer w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
                    />
                    <label
                      htmlFor="field-sede-operativa"
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--hexa-text-muted)] pointer-events-none transition-all duration-200 peer-focus:top-1 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:text-[#7b3fff] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px]"
                    >
                      Sede Operativa
                    </label>
                  </div>
                </>
              )}

              {/* Common fields for both person and company */}
              <div className="relative">
                <input
                  id="field-pec"
                  type="email"
                  value={pec}
                  onChange={(e) => setPec(e.target.value)}
                  placeholder=" "
                  className="peer w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition"
                />
                <label
                  htmlFor="field-pec"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--hexa-text-muted)] pointer-events-none transition-all duration-200 peer-focus:top-1 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:text-[#7b3fff] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px]"
                >
                  PEC
                </label>
              </div>

              {/* Collegamenti - like Task modal */}
              <div className="relative group/field">
                <div className={`w-full min-h-[34px] rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] px-2.5 flex flex-wrap items-center gap-1.5 focus-within:border-[var(--hexa-border)] transition ${linkedContacts.length > 0 || contactSearchInput ? 'pt-4 pb-1' : 'py-1.5'}`}>
                  {/* Badge contatti selezionati */}
                  {linkedContacts.map((contactId) => {
                    const contact = effectiveContacts.find((c) => c.id === contactId)
                    return (
                      <Badge
                        key={contactId}
                        className="bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white cursor-pointer text-[10px] shrink-0"
                        onClick={() => removeLinkedContact(contactId)}
                      >
                        {contact?.name}
                        <X className="w-2.5 h-2.5 ml-0.5" />
                      </Badge>
                    )
                  })}

                  {/* Input di ricerca senza bordo */}
                  <input
                    type="text"
                    placeholder=" "
                    value={contactSearchInput}
                    onChange={(e) => setContactSearchInput(e.target.value)}
                    className="flex-1 min-w-[90px] bg-transparent border-none text-[var(--hexa-text-primary)] pt-0 pb-0 text-xs focus:outline-none"
                  />
                </div>
                <label className={`absolute z-10 left-2.5 text-xs pointer-events-none transition-all duration-200 group-focus-within/field:top-1 group-focus-within/field:translate-y-0 group-focus-within/field:!text-[11px] group-focus-within/field:text-[#7b3fff] ${linkedContacts.length > 0 || contactSearchInput ? 'top-1 translate-y-0 !text-[11px] text-[var(--hexa-text-muted)]' : 'top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]'}`}>
                  Collegamenti
                </label>

                {/* Dropdown con contatti filtrati */}
                {contactSearchInput && filteredContacts.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-[var(--hexa-modal-bg)] border border-[var(--hexa-border)] rounded-lg shadow-xl max-h-32 overflow-y-auto scrollbar-hexa">
                    {filteredContacts.map((contact) => (
                      <div
                        key={contact.id}
                        onClick={() => addLinkedContact(contact.id)}
                        className="px-2.5 py-1.5 hover:bg-[var(--hexa-button-cancel)] cursor-pointer text-[var(--hexa-text-primary)] text-xs flex items-center justify-between"
                      >
                        <span>{contact.name}</span>
                        <Badge variant="secondary" className="bg-[var(--hexa-border)] text-[var(--hexa-text-secondary)] text-[10px]">
                          {contact.type === "person" ? "Persona" : "Azienda"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* SECTION 3 - Tag */}
        <div className="relative group/field">
          <div className={`w-full min-h-[34px] rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] px-2.5 flex flex-wrap items-center gap-1.5 focus-within:border-[var(--hexa-border)] transition ${selectedTags.length > 0 || tagInputValue ? 'pt-4 pb-1' : 'py-1.5'}`}>
            {/* Tag chip selezionati */}
            {selectedTags.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border"
                style={getTagChipStyles(tag.color, theme)}
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tag.color }}
                />
                {tag.name}
                <button
                  type="button"
                  onClick={() => handleTagRemove(tag.id)}
                  className="ml-0.5 hover:opacity-70 transition"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}

            {/* Input per cercare/creare tag */}
            <input
              ref={tagInputRef}
              type="text"
              placeholder=" "
              value={tagInputValue}
              onChange={(e) => {
                setTagInputValue(e.target.value)
                setShowTagDropdown(true)
              }}
              onFocus={() => setShowTagDropdown(true)}
              className="flex-1 min-w-[80px] bg-transparent border-none text-[var(--hexa-text-primary)] pt-0 pb-0 text-xs focus:outline-none"
            />
          </div>
          <label className={`absolute z-10 left-2.5 text-xs pointer-events-none transition-all duration-200 group-focus-within/field:top-1 group-focus-within/field:translate-y-0 group-focus-within/field:!text-[11px] group-focus-within/field:text-[#7b3fff] ${selectedTags.length > 0 || tagInputValue ? 'top-1 translate-y-0 !text-[11px] text-[var(--hexa-text-muted)]' : 'top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]'}`}>
            Tag
          </label>

          {/* Dropdown tag */}
          {showTagDropdown && (tagInputValue || filteredTags.length > 0) && (
            <div
              ref={tagDropdownRef}
              className="absolute z-50 w-full mt-1 bg-[var(--hexa-modal-bg)] border border-[var(--hexa-border)] rounded-lg shadow-xl max-h-32 overflow-y-auto scrollbar-hexa"
            >
              {/* Tag esistenti filtrati */}
              {filteredTags.map((tag) => (
                <div
                  key={tag.id}
                  onClick={() => handleTagSelect(tag)}
                  className="px-2.5 py-1.5 hover:bg-[var(--hexa-button-cancel)] cursor-pointer flex items-center gap-2"
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: tag.color }}
                  />
                  <span className="text-xs text-[var(--hexa-text-primary)]">{tag.name}</span>
                </div>
              ))}

              {/* Opzione per creare nuovo tag */}
              {showCreateOption && (
                <div
                  onClick={handleCreateTag}
                  className="px-2.5 py-1.5 hover:bg-[var(--hexa-button-cancel)] cursor-pointer flex items-center gap-2 border-t border-[var(--hexa-border)]"
                >
                  <Plus className="w-3 h-3 text-[var(--hexa-purple)]" />
                  <span className="text-xs text-[var(--hexa-text-primary)]">
                    Crea "<span className="font-medium text-[var(--hexa-purple)]">{tagInputValue.trim()}</span>"
                  </span>
                </div>
              )}

              {/* Messaggio se nessun risultato e non si può creare */}
              {filteredTags.length === 0 && !showCreateOption && tagInputValue && (
                <div className="px-2.5 py-1.5 text-xs text-[var(--hexa-text-muted)]">
                  Nessun tag trovato
                </div>
              )}
            </div>
          )}
        </div>

        {/* SECTION 4 - Note */}
        <div className="relative">
          <textarea
            id="field-note"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder=" "
            rows={3}
            className="peer w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 pt-3.5 pb-0.5 text-xs focus:outline-none focus:border-[var(--hexa-border)] transition resize-y min-h-[60px] max-h-[150px]"
          />
          <label
            htmlFor="field-note"
            className="absolute left-3 top-3 text-xs text-[var(--hexa-text-muted)] pointer-events-none transition-all duration-200 peer-placeholder-shown:top-3 peer-placeholder-shown:text-xs peer-focus:top-1 peer-focus:text-[11px] peer-focus:text-[#7b3fff] peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:text-[11px]"
          >
            Note
          </label>
        </div>

        {/* SECTION 5 - Allegati */}
        <div>
          {/* Header con label e pulsante + */}
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-medium text-[var(--hexa-text-primary)] flex items-center gap-2">
              <Paperclip className="h-3.5 w-3.5" />
              <span>Allegati</span>
              {uploading && <Loader2 className="h-3 w-3 animate-spin text-[var(--hexa-purple)]" />}
            </label>
            <label
              className={`w-7 h-7 rounded-md bg-[var(--hexa-surface-elevated)] border border-[var(--hexa-border)] flex items-center justify-center cursor-pointer hover:bg-[var(--hexa-surface-hover)] transition ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
            >
              <Plus className="h-3.5 w-3.5 text-[var(--hexa-purple)]" />
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileInputChange}
                className="hidden"
                disabled={uploading}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.gif,.webp,.zip,.rar"
              />
            </label>
          </div>

          {/* File chips */}
          {loadingAttachments ? (
            <div className="flex items-center gap-2 mt-2 text-xs text-[var(--hexa-text-muted)]">
              <Loader2 className="h-3 w-3 animate-spin" />
              Caricamento allegati...
            </div>
          ) : attachments.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {attachments.map((file) => (
                <div
                  key={file.id}
                  className="inline-flex items-center gap-1.5 py-1 px-2.5 rounded-md bg-[var(--hexa-surface-elevated)] border border-[var(--hexa-border)] max-w-full"
                >
                  <FileText className="h-3 w-3 text-[var(--hexa-purple)] opacity-60 flex-shrink-0" />
                  <span
                    onClick={() => handleDownloadAttachment(file)}
                    className="text-xs text-[var(--hexa-text-primary)] truncate hover:underline cursor-pointer"
                  >
                    {file.name}
                  </span>
                  {file.file_size && (
                    <span className="text-[11px] text-[var(--hexa-text-muted)] flex-shrink-0">
                      {Math.round(file.file_size / 1024)} KB
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDeleteAttachment(file.id)}
                    className="text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-primary)] transition flex-shrink-0"
                    aria-label="Elimina"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-[var(--hexa-border)] px-4 py-3">
        {/* Errore inline */}
        {(saveError || duplicateContact) && (
          <div role="alert" className="mb-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
            {duplicateContact ? "Esiste già un contatto con questo nominativo." : saveError}
          </div>
        )}

        <div className="flex gap-2.5">
          {/* Annulla Mini-Card */}
          <div className="flex-1 bg-[var(--hexa-button-cancel)] rounded-lg p-2.5 flex items-center justify-center border border-[var(--hexa-border)]">
            <button
              type="button"
              onClick={handleAttemptClose}
              disabled={isSaving}
              className="w-full text-[var(--hexa-button-cancel-text)] hover:text-[var(--hexa-text-primary)] disabled:opacity-50"
            >
              Annulla
            </button>
          </div>

          {/* Salva Mini-Card */}
          <div className="flex-1 rounded-lg flex items-center justify-center">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !!duplicateContact}
              className="w-full h-full bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white rounded-lg py-2.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {isSaving ? "Salvataggio..." : (mode === "create" ? "Salva contatto" : "Salva modifiche")}
            </button>
          </div>
        </div>
      </div>
    </div>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent className="bg-[var(--hexa-surface)] border-[var(--hexa-border)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[var(--hexa-text-primary)]">
              Modifiche non salvate
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--hexa-text-secondary)]">
              Hai delle modifiche non salvate. Se esci ora verranno perse.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleDiscardAndClose}
              className="bg-[var(--hexa-surface-elevated)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)]"
            >
              Esci senza salvare
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSaveAndClose}
              className="bg-[var(--hexa-purple)] text-white hover:bg-[var(--hexa-purple-hover)]"
            >
              Salva e chiudi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
})

// Backwards-compatible wrapper for existing code
export function NewContactModal({ onClose, onSave, availableContacts = [] }: NewContactModalProps) {
  return <ContactFormModal mode="create" onClose={onClose} onSave={onSave} availableContacts={availableContacts} />
}
